import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import type { User, School, AuthResponse } from '../types/api';

interface RegisterForm {
  schoolName: string;
  schoolCode: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

interface AuthContextValue {
  user: User | null;
  school: School | null;
  loading: boolean;
  login: (schoolCode: string, email: string, password: string) => Promise<AuthResponse>;
  register: (formData: RegisterForm) => Promise<AuthResponse>;
  registerSchool: (formData: RegisterForm) => Promise<AuthResponse>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

interface ProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<ProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedSchool = localStorage.getItem('school');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedSchool) setSchool(JSON.parse(savedSchool));
    }
    setLoading(false);
  }, []);

  const persistSession = (data: AuthResponse): void => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.school) {
      localStorage.setItem('school', JSON.stringify(data.school));
    } else {
      localStorage.removeItem('school');
    }
    setUser(data.user);
    setSchool(data.school || null);
  };

  // schoolCode is optional — owner (superadmin) accounts authenticate
  // without one since they exist above any single tenant.
  const login = async (schoolCode: string, email: string, password: string): Promise<AuthResponse> => {
    const payload: Record<string, string> = { email, password };
    if (schoolCode && schoolCode.trim()) payload.schoolCode = schoolCode.trim();
    const { data } = await api.post<AuthResponse>('/auth/login', payload);
    persistSession(data);
    return data;
  };

  const registerSchool = async (formData: RegisterForm): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/auth/register-school', formData);
    persistSession(data);
    return data;
  };

  const logout = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school');
    setUser(null);
    setSchool(null);
  };

  return (
    <AuthContext.Provider value={{
      user, school, loading,
      login,
      register: registerSchool,
      registerSchool,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
